<template>
  <AppPageContainer :custom="1600" :min-height="0">
    <app-page-header design="new" :title="copy.title" compact />
    <main class="ui-page">
      <app-filter-toolbar>
        <app-select v-if="availableTags.length" v-model="selectedTagOptions" design="new" multiple :options="availableTags" label="label" :placeholder="copy.filterTitle" :aria-label="copy.filterTitle">
          <template #custom-option="tag"><app-status-badge design="new" compact :label="tag.label" :color="tag.color" /></template>
        </app-select>
        <template #selection>
          <app-filters :value="language" design="new" refined weight="medium" :filters="languages" name="release-language" :aria-label="copy.languageLabel" @change="selectLanguage" />
        </template>
        <template #actions>
          <AppIconButton :label="copy.refresh" tooltip-placement="left" :disabled="loading" @click="fetchReleases"><i class="pi pi-refresh" aria-hidden="true" /></AppIconButton>
        </template>
        <template v-if="selectedTags.length" #clear>
          <AppClearFiltersButton design="new" icon-only :label="copy.clearFilters" @clear="clearTags" />
        </template>
      </app-filter-toolbar>

      <AppContentSkeleton v-if="loading" layout="block" :label="copy.loading" />
      <AppErrorState v-else-if="error" :message="error" :retry-label="copy.retry" @retry="fetchReleases" />
      <app-no-results v-else-if="!releases.length" design="new" :title="selectedTags.length ? copy.emptyFiltered : copy.emptyTitle" text="" />
      <div v-else class="ui-stack release-list">
        <release-preview v-for="release in releases" :key="release.id" :release="release" :language="language" show-detail @open-detail="openReleaseDetail" />
      </div>
    </main>

    <release-detail-modal
      v-if="detailSlug"
      :release="detailRelease"
      :language="language"
      :loading="detailLoading"
      :error="detailError"
      @retry="fetchReleaseDetail"
      @closed="closeReleaseDetail"
    />
  </AppPageContainer>
</template>

<script>
import { getSupportRelease, getSupportReleases } from '@/http'
import { collectTags, normalizeResults } from './releaseState'
import AppContentSkeleton from '@/components/AppContentSkeleton.vue'
import AppErrorState from '@/components/AppErrorState.vue'
import AppIconButton from '@/components/AppIconButton.vue'
import ReleaseDetailModal from './ReleaseDetailModal'
import ReleasePreview from './ReleasePreview'

export default {
  name: 'TechSupportReleasesList',
  components: { AppContentSkeleton, AppErrorState, AppIconButton, ReleaseDetailModal, ReleasePreview },
  data() {
    return {
      availableTags: [],
      detailError: '',
      detailLoading: false,
      detailRelease: null,
      detailSlug: '',
      error: '',
      language: 'en',
      languages: [
        { value: 'en', label: 'English' },
        { value: 'ru', label: 'Русский' }
      ],
      loading: false,
      releases: [],
      selectedTags: []
    }
  },
  created() {
    this.fetchReleases()
  },
  computed: {
    selectedTagOptions: {
      get() { return this.selectedTags.map(id => this.availableTags.find(tag => tag.id === id)).filter(Boolean) },
      set(tags) { this.selectedTags = tags.map(tag => tag.id); this.fetchReleases() }
    },
    copy() {
      if (this.language === 'ru') {
        return {
          title: 'Релизы',
          loading: 'Загрузка релизов',
          languageLabel: 'Язык релиза',
          refresh: 'Обновить',
          filterTitle: 'Фильтр по разделу',
          clearFilters: 'Сбросить фильтры',
          retry: 'Повторить',
          emptyTitle: 'Опубликованных релизов пока нет',
          emptyFiltered: 'Для выбранных тегов обновлений не найдено.',
          empty: 'Опубликованные заметки о релизах появятся здесь.',
          loadError: 'Не удалось загрузить релизы. Попробуйте ещё раз.',
          detailError: 'Не удалось загрузить полную версию релиза.'
        }
      }
      return {
        title: 'Releases',
        loading: 'Loading releases',
        languageLabel: 'Release language',
        refresh: 'Refresh',
        filterTitle: 'Filter by area',
        clearFilters: 'Clear filters',
        retry: 'Retry',
        emptyTitle: 'No published releases',
        emptyFiltered: 'No updates match the selected tags.',
        empty: 'Published release notes will appear here.',
        loadError: 'Releases could not be loaded. Please try again.',
        detailError: 'The full release could not be loaded.'
      }
    }
  },
  methods: {
    clearTags() {
      this.selectedTags = []
      this.fetchReleases()
    },
    fetchReleases() {
      this.error = ''
      this.loading = true
      const params = { language: this.language, page_size: 100 }
      if (this.selectedTags.length) params.tags = this.selectedTags.join(',')
      return getSupportReleases(params)
        .then(response => {
          this.releases = normalizeResults(response.data)
          if (!this.selectedTags.length) this.availableTags = collectTags(this.releases)
        })
        .catch(() => {
          this.error = this.copy.loadError
        })
        .finally(() => { this.loading = false })
    },
    fetchReleaseDetail() {
      if (!this.detailSlug) return Promise.resolve()
      this.detailError = ''
      this.detailLoading = true
      return getSupportRelease(this.detailSlug, { language: this.language })
        .then(response => { this.detailRelease = response.data })
        .catch(() => { this.detailError = this.copy.detailError })
        .finally(() => { this.detailLoading = false })
    },
    openReleaseDetail(release) {
      this.detailSlug = release.slug
      this.detailRelease = null
      this.detailError = ''
      this.$nextTick(() => {
        this.$modal.show('tech-support-release-detail')
        this.fetchReleaseDetail()
      })
    },
    closeReleaseDetail() {
      this.detailSlug = ''
      this.detailRelease = null
      this.detailError = ''
    },
    selectLanguage(language) {
      if (language === this.language) return
      this.language = language
      this.fetchReleases()
      if (this.detailSlug) this.fetchReleaseDetail()
    }
  }
}
</script>

<style lang="scss" scoped>
.release-list :deep(.release-preview-card__title-row h2),
.release-list :deep(.release-preview-card__summary),
.release-list :deep(.release-preview-item__copy) { overflow-wrap: anywhere; }
.release-list :deep(.release-preview-card__title-row h2) { min-width: 0; }
</style>
